import { createColumnHelper } from '@tanstack/react-table';
import { IMergedHeadersReturn } from './index.headers';
import { IAdminxStore } from '../../stores';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { useRoute } from '@monorepo/tools/src/lib/hooks/tools/use-route';
import { useEffect } from 'react';
import { BidderDomainTableModel } from '../../models/bidder-domains.model';
import { numberFormatterWithCommas } from '@monorepo/tools/src/lib/utils/number';
import { useDownload } from '@monorepo/tools/src/lib/hooks/tools/use-download';
import { HttpStore } from '@monorepo/controlled/src/stores/http.store';
import { DispatchLogsTypes } from '@monorepo/controlled/src/hooks/use-logs';
import { BidderDomainsApi } from '../../apis/bidder-domains.api';
import { Tag } from '@monorepo/base/src/components/tag/tag';
import { mapToTagStatus, Status } from '../../enums/status';

const columnHelper = createColumnHelper<BidderDomainTableModel>();

export const useBidderDomainsHeaders = (): IMergedHeadersReturn<BidderDomainTableModel> => {
	const { settingsStore, toastsStore } = useStores<IAdminxStore>();
	const { currentRouteWithoutSpecialChars } = useRoute();
	const tableStore = settingsStore.pages.get(currentRouteWithoutSpecialChars)?.tableStore;

	useEffect(() => {
		if (tableStore && Object.keys(tableStore.getColumnVisibility()).length === 0) {
			tableStore.setDefaultColumnVisibility({
				campaign_id: true,
				status: true,
				paused_in_cache: true,
				domains_in_trash: true,
				black_list: true,
				white_list: true,
				last_updated: true,
			});

			tableStore?.setColumnVisibility(tableStore.getDefaultColumnVisibility());

			if (tableStore.getSortingBy().length === 0) {
				tableStore.setSortingBy([
					{
						id: 'last_updated',
						desc: true,
					},
				]);
			}
		}
	}, [tableStore]);

	const download = (id: number, pathname: string, extra?: string, tag?: string) => {
		toastsStore.addToast({ msg: 'Starting your download...', type: DispatchLogsTypes.Info });

		const fileNameMapping: Record<string, string> = {
			downloadCampaignDomains: 'Bidder_domains',
			downloadCampaignNotWorkingDomains: 'Bidder_paused_in_cache',
			downloadTrashCampaignDomains: 'Bidder_domains_in_trash',
			downloadCampaignDomainsByTag: 'Bidder_domains_by_tag',
		};

		useDownload({
			httpStore: new HttpStore<undefined, File | null>({
				httpFunc: () => BidderDomainsApi.download(`${pathname}?id=${id}${extra || ''}`) as unknown as Promise<File | null>,
			}),
			fileName: `${id}_${fileNameMapping[pathname]}${tag ? `_${tag}` : ''}_${new Date()
				.toISOString()
				.substring(0, 19)
				.replace('T', ' ')
				.replace(/:/g, '-')}`,
			extension: 'csv',
		}).catch(err => {
			toastsStore.addToast({ msg: 'Download Failed, try again later.', type: DispatchLogsTypes.Error });
			console.log(err);
		});
	};

	return {
		columns: [
			columnHelper.accessor('campaign_id', {
				header: 'Campaign number',
				cell: ({ row }) => (
					<div className="links">
						<a onClick={() => download(row.original.campaign_id, 'downloadCampaignDomains', '&tag=')}>
							{row.original.campaign_id}
						</a>
					</div>
				),
			}),
			columnHelper.accessor('status', {
				header: 'Status',
				cell: ({ row }) => {
					const status = row.getValue('status') as string | undefined;

					return <Tag type={mapToTagStatus(status?.toUpperCase().trim() as Status)}>{status?.trim()}</Tag>;
				},
			}),
			columnHelper.accessor('paused_in_cache', {
				header: 'Paused in cache',
				cell: ({ row }) => (
					<div className="links">
						{row.original.paused_in_cache && (
							<a onClick={() => download(row.original.campaign_id, 'downloadCampaignNotWorkingDomains')}>
								[{numberFormatterWithCommas.format(row.original.paused_in_cache)}]
							</a>
						)}
					</div>
				),
			}),
			columnHelper.accessor('domains_in_trash', {
				header: 'Domains in trash',
				cell: ({ row }) => (
					<div className="links">
						{row.original.domains_in_trash && (
							<a onClick={() => download(row.original.campaign_id, 'downloadTrashCampaignDomains')}>
								[{numberFormatterWithCommas.format(row.original.domains_in_trash)}]
							</a>
						)}
					</div>
				),
			}),
			columnHelper.accessor('black_list', {
				header: 'BL',
				cell: ({ row }) => (
					<div className="links">
						{row.original?.black_list?.map(i => (
							<a
								onClick={() =>
									download(
										row.original.campaign_id,
										'downloadCampaignDomainsByTag',
										`&list_type=BLACKLIST&tag=${i.tag}`,
										i.tag
									)
								}>
								{i.tag} ({numberFormatterWithCommas.format(i.counter)})
							</a>
						))}
					</div>
				),
			}),
			columnHelper.accessor('white_list', {
				header: 'WL',
				cell: ({ row }) => (
					<div className="links">
						{row.original?.white_list?.map(i => (
							<a
								onClick={() =>
									download(
										row.original.campaign_id,
										'downloadCampaignDomainsByTag',
										`&list_type=WHITELIST&tag=${i.tag}`,
										i.tag
									)
								}>
								{i.tag} ({numberFormatterWithCommas.format(i.counter)})
							</a>
						))}
					</div>
				),
			}),
			columnHelper.accessor('last_updated', { header: 'Last update' }),
		],
	};
};
